import { isRealIsoDate, normaliseEmail, normalisePhone, type ImportCsvRow } from "@teamna/shared";

/**
 * The CSV side of the member import, read in the browser. The API only ever sees rows.
 *
 * Three steps, each a pure function so the tests can hold them to account without a file picker:
 * `parseCsv` turns text into cells, `detectMapping` guesses which column holds what from the
 * header row, and `previewRows` turns the cells into import rows with the problems of each row and
 * the values that wait for a later setup step.
 *
 * Two kinds of sheet, from the business shape. A family sheet (`CSV_FIELDS`): one row for each
 * participant, with the member, the group, the sessions left and paid or owed. A client sheet
 * (`CLIENT_CSV_FIELDS`, feature O1, where clients book for themselves): one row for each client,
 * who is the person who attends, with an email, the package, the sessions left, the day the package
 * ends and paid or owed. Each function takes the field list, so a family sheet reads as it always did.
 */

export type CsvField =
  "memberName" | "phone" | "participantName" | "groupName" | "sessionsLeft" | "paid" | "email" | "packageName" | "endsOn";

/** The columns of a family sheet. */
export const CSV_FIELDS: CsvField[] = ["memberName", "phone", "participantName", "groupName", "sessionsLeft", "paid"];

/** The columns of a client sheet, where clients book for themselves (O1). */
export const CLIENT_CSV_FIELDS: CsvField[] = ["memberName", "phone", "email", "packageName", "sessionsLeft", "endsOn", "paid"];

/** The columns of the sheet of a business: families, or clients who book for themselves. */
export function csvFields(forChildren: boolean): CsvField[] {
  return forChildren ? CSV_FIELDS : CLIENT_CSV_FIELDS;
}

function emptyMapping(): CsvMapping {
  return {
    memberName: null,
    phone: null,
    participantName: null,
    groupName: null,
    sessionsLeft: null,
    paid: null,
    email: null,
    packageName: null,
    endsOn: null,
  };
}

/** Column index per field, or null when the file has no such column. */
export type CsvMapping = Record<CsvField, number | null>;

export type CsvTable = { headers: string[]; rows: string[][]; hasHeader: boolean };

/**
 * RFC 4180 cells: quoted fields, doubled quotes, line breaks inside quotes. The delimiter is a comma
 * unless the first line has more semicolons or tabs (a spreadsheet saved with a European locale).
 */
export function parseCsv(text: string): string[][] {
  const source = text.replace(/^﻿/, "");
  const firstLine = source.split(/\r?\n/, 1)[0] ?? "";
  const counts = { ",": 0, ";": 0, "\t": 0 };
  for (const char of firstLine) if (char in counts) counts[char as keyof typeof counts] += 1;
  const delimiter = counts[";"] > counts[","] && counts[";"] >= counts["\t"] ? ";" : counts["\t"] > counts[","] ? "\t" : ",";

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]!;
    if (quoted) {
      if (char === '"') {
        if (source[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += char;
      continue;
    }
    if (char === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (char === delimiter) {
      row.push(cell.trim());
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[i + 1] === "\n") i += 1;
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = "";
    } else cell += char;
  }
  if (cell !== "" || row.length > 0) {
    row.push(cell.trim());
    rows.push(row);
  }
  return rows.filter((cells) => cells.some((value) => value !== ""));
}

function squash(value: string): string {
  return value.toLowerCase().replace(/[^a-z]/g, "");
}

/** Words a header may use for each field, before the business's own labels are added. */
const SYNONYMS: Record<CsvField, string[]> = {
  memberName: ["member", "membername", "parent", "parentname", "guardian", "mother", "father", "client", "clientname", "name", "fullname", "contactname", "family", "account"],
  phone: ["phone", "mobile", "mobilenumber", "phonenumber", "number", "whatsapp", "whatsappnumber", "tel", "telephone", "contactnumber", "cell"],
  participantName: ["participant", "participantname", "child", "childname", "kid", "student", "studentname", "swimmer", "player", "gymnast", "athlete", "attendee"],
  groupName: ["group", "groupname", "level", "class", "classname", "team", "squad", "course"],
  sessionsLeft: ["sessionsleft", "sessions", "sessionsremaining", "remaining", "balance", "left", "classesleft", "lessonsleft", "credits"],
  paid: ["paid", "paidorowed", "payment", "paymentstatus", "owed", "owes", "status", "fees", "feestatus"],
  email: ["email", "emailaddress", "mail", "emailid"],
  packageName: ["package", "packagename", "pack", "packname", "plan", "membership", "product", "bundle"],
  endsOn: ["endson", "enddate", "ends", "end", "expires", "expireson", "expiry", "expirydate", "validuntil", "valid", "until", "lastday"],
};

/**
 * Participant before member: "Swimmer name" holds "name", and it is not the member's name. Email,
 * package and end date before member too: "Client email" and "Membership" are not the client's name.
 */
const CONTAINS_ORDER: CsvField[] = ["participantName", "phone", "email", "packageName", "endsOn", "groupName", "sessionsLeft", "paid", "memberName"];

/**
 * Which column holds what, from the header row. An exact word wins over a word inside a longer
 * header; each column is used once. The business's own labels count as words too, so a pilates
 * studio's "Client" column is found. When the member and the participant have the same label (a
 * studio where the client is the one who attends) the column goes to the member, and rows with no
 * participant column use the member's name.
 */
export function detectMapping(
  headers: string[],
  labels: Partial<Record<CsvField, string[]>> = {},
  fields: CsvField[] = CSV_FIELDS,
): CsvMapping {
  const words: Record<CsvField, string[]> = { ...SYNONYMS };
  for (const field of fields) {
    for (const word of labels[field] ?? []) {
      const squashed = squash(word);
      if (squashed && !words[field].includes(squashed)) words[field] = [squashed, ...words[field], `${squashed}name`];
    }
  }
  const mapping = emptyMapping();
  const used = new Set<number>();
  const squashed = headers.map(squash);
  const order = CONTAINS_ORDER.filter((field) => fields.includes(field));

  for (const field of ["memberName", ...order.filter((f) => f !== "memberName")] as CsvField[]) {
    const index = squashed.findIndex((header, i) => !used.has(i) && header !== "" && words[field].includes(header));
    if (index >= 0) {
      mapping[field] = index;
      used.add(index);
    }
  }
  for (const field of order) {
    if (mapping[field] !== null) continue;
    const index = squashed.findIndex(
      (header, i) => !used.has(i) && header !== "" && words[field].some((word) => word.length >= 4 && header.includes(word)),
    );
    if (index >= 0) {
      mapping[field] = index;
      used.add(index);
    }
  }
  return mapping;
}

/**
 * Splits the header from the data. The first row is a header when none of its cells is a phone
 * number and at least one names a known field; otherwise every row is data and the columns are
 * numbered, and the mapping is guessed from the first data row (the column with a number is the
 * phone, the first text column the member).
 */
export function readTable(
  cells: string[][],
  labels: Partial<Record<CsvField, string[]>> = {},
  fields: CsvField[] = CSV_FIELDS,
): { table: CsvTable; mapping: CsvMapping } {
  const first = cells[0] ?? [];
  const width = Math.max(0, ...cells.map((row) => row.length));
  const firstHasPhone = first.some((value) => normalisePhone(value) !== null);
  const guess = detectMapping(first, labels, fields);
  const hasHeader = !firstHasPhone && Object.values(guess).some((value) => value !== null);
  if (hasHeader) {
    const headers = Array.from({ length: width }, (_, i) => first[i] || `Column ${i + 1}`);
    return { table: { headers, rows: cells.slice(1), hasHeader }, mapping: guess };
  }
  const headers = Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
  const mapping = emptyMapping();
  const phoneAt = first.findIndex((value) => normalisePhone(value) !== null);
  if (phoneAt >= 0) mapping.phone = phoneAt;
  const nameAt = first.findIndex((value, i) => i !== phoneAt && /[a-z]/i.test(value));
  if (nameAt >= 0) mapping.memberName = nameAt;
  return { table: { headers, rows: cells, hasHeader }, mapping };
}

export type PreviewRow = {
  /** 1-based line in the file, as a spreadsheet shows it. */
  line: number;
  memberName: string;
  phone: string;
  participantName: string;
  groupName: string;
  sessionsLeft: string;
  paid: string;
  /** A client sheet only (O1). Empty on a family sheet. */
  email: string;
  packageName: string;
  endsOn: string;
  /** The row as the API takes it, when it has no problems. */
  row: ImportCsvRow | null;
  errors: string[];
  /**
   * What waits for a later setup step, which does not stop the row: a group that does not exist yet
   * (setup step 4), sessions left before any package exists (step 5). The API keeps the values and
   * uses them when the group or the first package is saved.
   */
  notes: string[];
};

const PAID = new Set(["paid", "yes", "y", "true", "1", "done"]);
const OWED = new Set(["owed", "owes", "owing", "no", "n", "false", "0", "unpaid", "due", "notpaid"]);

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/**
 * A date as a spreadsheet writes it, as `yyyy-MM-dd`. Undefined for an empty cell, null when it is
 * not a real date. Numbers are day first, as in the UAE: 20/12/2026, 20-12-26, 20.12.2026. Also
 * 2026-12-20, "20 Dec 2026" and "December 20, 2026". A two-digit year is in the 2000s.
 */
export function readSheetDate(value: string): string | undefined | null {
  const text = value.trim().toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
  if (text === "") return undefined;
  const iso = (year: number, month: number, day: number) => {
    const full = year < 100 ? 2000 + year : year;
    const result = `${String(full).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return full >= 2000 && full <= 2100 && isRealIsoDate(result) ? result : null;
  };
  let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ t].*)?$/.exec(text);
  if (match) return iso(Number(match[1]), Number(match[2]), Number(match[3]));
  match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(text);
  if (match) return iso(Number(match[3]), Number(match[2]), Number(match[1]));
  const monthOf = (word: string) => {
    const index = MONTHS.indexOf(word.slice(0, 3));
    return index >= 0 && (word.length === 3 || word.startsWith(MONTHS[index]!)) ? index + 1 : 0;
  };
  match = /^(\d{1,2}) ([a-z]+)\.? (\d{2}|\d{4})$/.exec(text);
  if (match && monthOf(match[2]!)) return iso(Number(match[3]), monthOf(match[2]!), Number(match[1]));
  match = /^([a-z]+)\.? (\d{1,2}) (\d{2}|\d{4})$/.exec(text);
  if (match && monthOf(match[1]!)) return iso(Number(match[3]), monthOf(match[1]!), Number(match[2]));
  return null;
}

/** "paid" or "owed", in the words a spreadsheet uses. Undefined for an empty cell, null when unreadable. */
export function readPaid(value: string): boolean | undefined | null {
  const squashed = squash(value) || value.trim();
  if (squashed === "") return undefined;
  if (PAID.has(squashed)) return true;
  if (OWED.has(squashed)) return false;
  return null;
}

/** A package the import can name, as the Packages page lists it. */
export type CsvPackage = { name: string; newClientsOnly?: boolean };

export function previewRows(
  table: CsvTable,
  mapping: CsvMapping,
  /**
   * `groupNames` is null while the groups load: no "waits" note until they are known. A client
   * sheet (`forSelf`) also takes the packages (null while they load) and today's date.
   */
  options: {
    groupNames: string[] | null;
    groupWord: string;
    hasPackages?: boolean;
    forSelf?: boolean;
    packages?: CsvPackage[] | null;
    today?: string;
  },
): PreviewRow[] {
  const forSelf = options.forSelf === true;
  const groups = options.groupNames ? new Set(options.groupNames.map((name) => name.trim().toLowerCase())) : null;
  const packages = options.packages ? new Map(options.packages.map((row) => [row.name.trim().toLowerCase(), row])) : null;
  const seen = new Map<string, number>();
  const at = (cells: string[], field: CsvField) => {
    const index = mapping[field];
    return index === null ? "" : (cells[index] ?? "").trim();
  };

  return table.rows.map((cells, i) => {
    const line = i + (table.hasHeader ? 2 : 1);
    const memberName = at(cells, "memberName");
    const phone = at(cells, "phone");
    // A client is the person who attends: the client sheet has no participant column.
    const participantName = (forSelf ? "" : at(cells, "participantName")) || memberName;
    const groupName = forSelf ? "" : at(cells, "groupName");
    const sessionsLeft = at(cells, "sessionsLeft");
    const paid = at(cells, "paid");
    const email = forSelf ? at(cells, "email") : "";
    const packageName = forSelf ? at(cells, "packageName") : "";
    const endsOn = forSelf ? at(cells, "endsOn") : "";
    const errors: string[] = [];
    const notes: string[] = [];

    if (!memberName) errors.push("No name");
    const phoneE164 = normalisePhone(phone);
    if (!phone) errors.push("No mobile number");
    else if (!phoneE164 || phone.length < 6) errors.push("Not a mobile number we can read");
    if (groupName && groups && !groups.has(groupName.toLowerCase())) {
      notes.push(`Waits for the ${options.groupWord} "${groupName}"`);
    }
    let sessions: number | undefined;
    if (sessionsLeft !== "") {
      const parsed = Number(sessionsLeft);
      if (!Number.isInteger(parsed) || parsed < 0 || parsed > 200) errors.push("Sessions left must be a number from 0 to 200");
      else {
        sessions = parsed;
        if (options.hasPackages === false) {
          // A client's sessions go on the hidden Opening balance package, as Add sessions does.
          if (forSelf) notes.push("No package yet: kept as an opening balance, paid");
          else notes.push("Sessions left wait for a package");
        }
      }
    }
    const paidValue = readPaid(paid);
    if (paidValue === null) errors.push('Write "paid" or "owed"');

    const emailValue = email ? normaliseEmail(email) : null;
    if (email && !emailValue) errors.push("Not an email address we can read");
    if (packageName && packages) {
      const found = packages.get(packageName.toLowerCase());
      if (!found) errors.push("No package with this name");
      else if (found.newClientsOnly) errors.push("An intro offer cannot hold sessions left. Use another package");
    }
    const endValue = readSheetDate(endsOn);
    if (endValue === null) errors.push("Not a date we can read. Write it as 31/12/2026");
    else if (endValue && options.today && endValue < options.today) errors.push("The end date is before today");
    if ((packageName || endsOn) && sessionsLeft === "") errors.push("Add the sessions left of this package");

    if (phoneE164 && participantName) {
      // A client sheet has one person on each number.
      const key = forSelf ? phoneE164 : `${phoneE164}|${participantName.toLowerCase()}`;
      const earlier = seen.get(key);
      if (earlier !== undefined) errors.push(`Same person as line ${earlier}`);
      else seen.set(key, line);
    }

    const row: ImportCsvRow | null =
      errors.length === 0
        ? {
            memberName,
            phone,
            participantName,
            ...(groupName ? { groupName } : {}),
            ...(sessions !== undefined ? { sessionsLeft: sessions } : {}),
            ...(paidValue !== undefined && paidValue !== null ? { paid: paidValue } : {}),
            ...(emailValue ? { email: emailValue } : {}),
            ...(packageName ? { packageName } : {}),
            ...(endValue ? { endsOn: endValue } : {}),
          }
        : null;
    return { line, memberName, phone, participantName, groupName, sessionsLeft, paid, email, packageName, endsOn, row, errors, notes };
  });
}
